import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Save, ArrowLeft, Image as ImageIcon, X, ChevronDown, Settings, Bold, Italic, List, ListOrdered, Quote, Code, Heading1, Heading2, Heading3, Link as LinkIcon, FileCode } from 'lucide-react';
import axios from 'axios';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import TipTapLink from '@tiptap/extension-link';
import AdminLayout from '@/components/AdminLayout';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiptap Menu Bar Component
const MenuBar = ({ editor, onToggleHtmlMode, isHtmlMode }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1 p-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
      {!isHtmlMode && (
        <>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={`p-2 rounded-lg hover:bg-slate-200 transition-colors ${editor.isActive('heading', { level: 1 }) ? 'bg-slate-200 text-[#6F42F0]' : 'text-slate-600'}`}
            title="Heading 1"
          >
            <Heading1 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={`p-2 rounded-lg hover:bg-slate-200 transition-colors ${editor.isActive('heading', { level: 2 }) ? 'bg-slate-200 text-[#6F42F0]' : 'text-slate-600'}`}
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={`p-2 rounded-lg hover:bg-slate-200 transition-colors ${editor.isActive('heading', { level: 3 }) ? 'bg-slate-200 text-[#6F42F0]' : 'text-slate-600'}`}
            title="Heading 3"
          >
            <Heading3 className="w-4 h-4" />
          </button>
          
          <div className="w-px h-6 bg-slate-300 mx-1" />
          
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-2 rounded-lg hover:bg-slate-200 transition-colors ${editor.isActive('bold') ? 'bg-slate-200 text-[#6F42F0]' : 'text-slate-600'}`}
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-2 rounded-lg hover:bg-slate-200 transition-colors ${editor.isActive('italic') ? 'bg-slate-200 text-[#6F42F0]' : 'text-slate-600'}`}
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </button>
          
          <div className="w-px h-6 bg-slate-300 mx-1" />
          
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-2 rounded-lg hover:bg-slate-200 transition-colors ${editor.isActive('bulletList') ? 'bg-slate-200 text-[#6F42F0]' : 'text-slate-600'}`}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-2 rounded-lg hover:bg-slate-200 transition-colors ${editor.isActive('orderedList') ? 'bg-slate-200 text-[#6F42F0]' : 'text-slate-600'}`}
            title="Ordered List"
          >
            <ListOrdered className="w-4 h-4" />
          </button>
          
          <div className="w-px h-6 bg-slate-300 mx-1" />
          
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`p-2 rounded-lg hover:bg-slate-200 transition-colors ${editor.isActive('blockquote') ? 'bg-slate-200 text-[#6F42F0]' : 'text-slate-600'}`}
            title="Quote"
          >
            <Quote className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={`p-2 rounded-lg hover:bg-slate-200 transition-colors ${editor.isActive('codeBlock') ? 'bg-slate-200 text-[#6F42F0]' : 'text-slate-600'}`}
            title="Code Block"
          >
            <Code className="w-4 h-4" />
          </button>
          
          <div className="w-px h-6 bg-slate-300 mx-1" />
          
          <button
            type="button"
            onClick={addLink}
            className={`p-2 rounded-lg hover:bg-slate-200 transition-colors ${editor.isActive('link') ? 'bg-slate-200 text-[#6F42F0]' : 'text-slate-600'}`}
            title="Add Link"
          >
            <LinkIcon className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={addImage}
            className="p-2 rounded-lg hover:bg-slate-200 transition-colors text-slate-600"
            title="Add Image"
          >
            <ImageIcon className="w-4 h-4" />
          </button>
          
          <div className="w-px h-6 bg-slate-300 mx-1" />
        </>
      )}
      
      <button
        type="button"
        onClick={onToggleHtmlMode}
        className={`p-2 rounded-lg hover:bg-slate-200 transition-colors flex items-center gap-1.5 ${isHtmlMode ? 'bg-[#6F42F0] text-white hover:bg-[#5B32D6]' : 'text-slate-600'}`}
        title={isHtmlMode ? "Switch to Visual Editor" : "Switch to HTML Mode"}
      >
        <FileCode className="w-4 h-4" />
        <span className="text-xs font-medium">{isHtmlMode ? 'Visual' : 'HTML'}</span>
      </button>
    </div>
  );
};

const AdminBlogEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [seoOpen, setSeoOpen] = useState(false);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    category_id: '',
    status: 'draft',
    seo: {
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      og_title: '',
      og_description: '',
      og_image: '',
      canonical_url: ''
    }
  });

  const [imagePreview, setImagePreview] = useState('');

  // Initialize Tiptap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Placeholder.configure({
        placeholder: 'Start writing your blog post...'
      }),
      Image,
      TipTapLink.configure({
        openOnClick: false
      })
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      if (!isHtmlMode) {
        const html = editor.getHTML();
        setFormData(prev => ({ ...prev, content: html }));
        setHtmlContent(html);
      }
    }
  });

  // Toggle between HTML and Visual mode
  const toggleHtmlMode = () => {
    if (isHtmlMode) {
      // Switching from HTML to Visual - apply HTML content to editor
      if (editor) {
        editor.commands.setContent(htmlContent);
        setFormData(prev => ({ ...prev, content: htmlContent }));
      }
    } else {
      // Switching from Visual to HTML - get content from editor
      if (editor) {
        setHtmlContent(editor.getHTML());
      }
    }
    setIsHtmlMode(!isHtmlMode);
  };

  // Handle HTML textarea change
  const handleHtmlChange = (e) => {
    const value = e.target.value;
    setHtmlContent(value);
    setFormData(prev => ({ ...prev, content: value }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesRes = await axios.get(`${API}/categories`);
        setCategories(categoriesRes.data);

        if (isEditing) {
          const blogRes = await axios.get(`${API}/blogs/${id}`, { headers: getAuthHeaders() });
          const blog = blogRes.data;
          setFormData({
            title: blog.title || '',
            slug: blog.slug || '',
            content: blog.content || '',
            excerpt: blog.excerpt || '',
            featured_image: blog.featured_image || '',
            category_id: blog.category_id || '',
            status: blog.status || 'draft',
            seo: blog.seo || {
              meta_title: '',
              meta_description: '',
              meta_keywords: '',
              og_title: '',
              og_description: '',
              og_image: '',
              canonical_url: ''
            }
          });
          // Set HTML content for HTML mode
          setHtmlContent(blog.content || '');
          if (blog.featured_image) {
            setImagePreview(blog.featured_image.startsWith('/api') 
              ? `${process.env.REACT_APP_BACKEND_URL}${blog.featured_image}` 
              : blog.featured_image);
          }
          // Set editor content
          if (editor && blog.content) {
            editor.commands.setContent(blog.content);
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
        if (error.response?.status === 404) {
          navigate('/admin/blogs');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEditing, navigate, getAuthHeaders, editor]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSeoChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      seo: { ...prev.seo, [field]: value }
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result;
        setImagePreview(base64);
        
        // Upload to server
        const response = await axios.post(
          `${API}/upload`,
          { image: base64, filename: file.name },
          { headers: getAuthHeaders() }
        );
        
        handleChange('featured_image', response.data.url);
        toast.success('Image uploaded successfully');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
    }
  };

  const handleImageUrlChange = (url) => {
    handleChange('featured_image', url);
    setImagePreview(url);
  };

  const removeImage = () => {
    handleChange('featured_image', '');
    setImagePreview('');
  };

  const handleSubmit = async (status) => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!formData.content.trim() || formData.content === '<p></p>') {
      toast.error('Content is required');
      return;
    }

    setSaving(true);
    
    try {
      const data = {
        ...formData,
        status: status || formData.status
      };

      if (isEditing) {
        await axios.put(`${API}/blogs/${id}`, data, { headers: getAuthHeaders() });
        toast.success('Blog post updated successfully');
      } else {
        await axios.post(`${API}/blogs`, data, { headers: getAuthHeaders() });
        toast.success('Blog post created successfully');
      }
      
      navigate('/admin/blogs');
    } catch (error) {
      console.error('Failed to save blog:', error);
      toast.error('Failed to save blog post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6F42F0]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="page-transition max-w-5xl" data-testid="blog-edit-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link 
              to="/admin/blogs"
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <div>
              <h1 className="font-['Manrope'] text-2xl md:text-3xl font-bold text-slate-900">
                {isEditing ? 'Edit Post' : 'Create New Post'}
              </h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => handleSubmit('draft')}
              disabled={saving}
              className="rounded-full"
              data-testid="save-draft-btn"
            >
              Save Draft
            </Button>
            <Button
              onClick={() => handleSubmit('published')}
              disabled={saving}
              className="btn-primary rounded-full text-white"
              data-testid="publish-btn"
            >
              {saving ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Publish
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-slate-700 font-medium">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Enter blog post title"
                className="h-14 text-lg font-medium rounded-xl"
                data-testid="title-input"
              />
            </div>

            {/* Content Editor */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Content</Label>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <MenuBar editor={editor} onToggleHtmlMode={toggleHtmlMode} isHtmlMode={isHtmlMode} />
                {isHtmlMode ? (
                  <textarea
                    value={htmlContent}
                    onChange={handleHtmlChange}
                    className="w-full min-h-[400px] p-5 font-mono text-sm text-slate-800 focus:outline-none resize-none"
                    placeholder="<h2>Your heading</h2>&#10;<p>Your paragraph content here...</p>"
                    data-testid="html-editor"
                  />
                ) : (
                  <EditorContent 
                    editor={editor} 
                    className="prose prose-lg max-w-none min-h-[400px] p-5 focus:outline-none"
                    data-testid="content-editor"
                  />
                )}
              </div>
              {isHtmlMode && (
                <p className="text-sm text-slate-500">
                  You can write raw HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;div&gt;, &lt;ul&gt;, etc.
                </p>
              )}
            </div>

            {/* Excerpt */}
            <div className="space-y-2">
              <Label htmlFor="excerpt" className="text-slate-700 font-medium">Excerpt</Label>
              <Textarea
                id="excerpt"
                value={formData.excerpt}
                onChange={(e) => handleChange('excerpt', e.target.value)}
                placeholder="Brief summary of your post (displayed in blog listing)"
                className="rounded-xl resize-none"
                rows={3}
                data-testid="excerpt-input"
              />
            </div>

            {/* SEO Settings */}
            <Collapsible open={seoOpen} onOpenChange={setSeoOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="flex items-center justify-between w-full p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  data-testid="seo-toggle"
                >
                  <div className="flex items-center gap-3">
                    <Settings className="w-5 h-5 text-slate-500" />
                    <span className="font-medium text-slate-700">SEO Settings</span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${seoOpen ? 'rotate-180' : ''}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">Meta Title</Label>
                    <Input
                      value={formData.seo.meta_title}
                      onChange={(e) => handleSeoChange('meta_title', e.target.value)}
                      placeholder="SEO title (defaults to post title)"
                      className="rounded-xl"
                      data-testid="seo-meta-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">Meta Keywords</Label>
                    <Input
                      value={formData.seo.meta_keywords}
                      onChange={(e) => handleSeoChange('meta_keywords', e.target.value)}
                      placeholder="keyword1, keyword2, keyword3"
                      className="rounded-xl"
                      data-testid="seo-meta-keywords"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm">Meta Description</Label>
                  <Textarea
                    value={formData.seo.meta_description}
                    onChange={(e) => handleSeoChange('meta_description', e.target.value)}
                    placeholder="Brief description for search engines (150-160 characters)"
                    className="rounded-xl resize-none"
                    rows={2}
                    data-testid="seo-meta-description"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">OG Title</Label>
                    <Input
                      value={formData.seo.og_title}
                      onChange={(e) => handleSeoChange('og_title', e.target.value)}
                      placeholder="OpenGraph title for social media"
                      className="rounded-xl"
                      data-testid="seo-og-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600 text-sm">OG Image URL</Label>
                    <Input
                      value={formData.seo.og_image}
                      onChange={(e) => handleSeoChange('og_image', e.target.value)}
                      placeholder="Image URL for social sharing"
                      className="rounded-xl"
                      data-testid="seo-og-image"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm">OG Description</Label>
                  <Textarea
                    value={formData.seo.og_description}
                    onChange={(e) => handleSeoChange('og_description', e.target.value)}
                    placeholder="Description for social media sharing"
                    className="rounded-xl resize-none"
                    rows={2}
                    data-testid="seo-og-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600 text-sm">Canonical URL</Label>
                  <Input
                    value={formData.seo.canonical_url}
                    onChange={(e) => handleSeoChange('canonical_url', e.target.value)}
                    placeholder="https://example.com/blog/your-post"
                    className="rounded-xl"
                    data-testid="seo-canonical-url"
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <Label className="text-slate-700 font-medium mb-3 block">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleChange('status', value)}
              >
                <SelectTrigger className="rounded-xl" data-testid="status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <Label className="text-slate-700 font-medium mb-3 block">Category</Label>
              <Select
                value={formData.category_id || "none"}
                onValueChange={(value) => handleChange('category_id', value === "none" ? "" : value)}
              >
                <SelectTrigger className="rounded-xl" data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No category</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Link 
                to="/admin/categories" 
                className="text-[#6F42F0] text-sm mt-3 inline-block hover:underline"
              >
                Manage categories
              </Link>
            </div>

            {/* Featured Image */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <Label className="text-slate-700 font-medium mb-3 block">Featured Image</Label>
              
              {imagePreview ? (
                <div className="relative mb-4">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-40 object-cover rounded-xl"
                  />
                  <button
                    onClick={removeImage}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    data-testid="remove-image-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#6F42F0] hover:bg-slate-50 transition-colors mb-4">
                  <ImageIcon className="w-8 h-8 text-slate-400 mb-2" />
                  <span className="text-slate-500 text-sm">Click to upload image</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-upload-input"
                  />
                </label>
              )}
              
              <div className="space-y-2">
                <Label className="text-slate-600 text-sm">Or enter image URL</Label>
                <Input
                  value={formData.featured_image}
                  onChange={(e) => handleImageUrlChange(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="rounded-xl"
                  data-testid="image-url-input"
                />
              </div>
            </div>

            {/* Slug */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <Label className="text-slate-700 font-medium mb-3 block">URL Slug</Label>
              <Input
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="auto-generated-from-title"
                className="rounded-xl"
                data-testid="slug-input"
              />
              <p className="text-slate-400 text-xs mt-2">
                Leave empty to auto-generate from title
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .ProseMirror {
          min-height: 400px;
          outline: none;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #94a3b8;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror h1 {
          font-family: 'Manrope', sans-serif;
          font-size: 2rem;
          font-weight: 700;
          margin: 1.5rem 0 1rem;
        }
        .ProseMirror h2 {
          font-family: 'Manrope', sans-serif;
          font-size: 1.5rem;
          font-weight: 600;
          margin: 1.25rem 0 0.75rem;
        }
        .ProseMirror h3 {
          font-family: 'Manrope', sans-serif;
          font-size: 1.25rem;
          font-weight: 600;
          margin: 1rem 0 0.5rem;
        }
        .ProseMirror p {
          margin-bottom: 1rem;
          line-height: 1.75;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
          margin-bottom: 1rem;
        }
        .ProseMirror blockquote {
          border-left: 4px solid #6F42F0;
          padding-left: 1rem;
          margin: 1rem 0;
          color: #475569;
          font-style: italic;
        }
        .ProseMirror pre {
          background: #1e293b;
          color: #e2e8f0;
          padding: 1rem;
          border-radius: 8px;
          overflow-x: auto;
          margin: 1rem 0;
        }
        .ProseMirror code {
          background: #f1f5f9;
          padding: 0.125rem 0.375rem;
          border-radius: 4px;
          font-size: 0.875em;
        }
        .ProseMirror pre code {
          background: transparent;
          padding: 0;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1rem 0;
        }
        .ProseMirror a {
          color: #6F42F0;
          text-decoration: underline;
        }
      `}</style>
    </AdminLayout>
  );
};

export default AdminBlogEdit;
